import React from 'react';
import CursorCoordinatesItem from './CursorCoordinatesItem';
import type { CursorCoordinatesItemProps } from './CursorCoordinatesItem';
import QACheckItem from './QACheckItem';
import type { QACheckItemProps } from './QACheckItem';

interface OverlayInstance {
  SeriesDescription?: string;
  StudyDate?: string;
  Modality?: string;
  ReferringPhysicianName?: string;
}

/**
 * §5.5. Upstream's arrays are replaced WHOLESALE, not patched. The 3.13 base is
 * `topLeft: [StudyDate, SeriesDescription]` and `bottomLeft: [WindowLevel, ZoomLevel]`
 * (extensions/cornerstone/src/customizations/viewportOverlayCustomization.tsx); the result
 * below matches develop, StudyDate having moved corner.
 *
 * A bare array suffices and no `$set` is needed: CustomizationService's _update:1064-1066
 * returns any value with no `$` key verbatim. That holds only because hasDollarKey:1209-1224
 * RECURSES into arrays and objects -- so no item key here may ever start with `$`, or the
 * array would be handed to immutability-helper as a command spec instead.
 *
 * `condition` is evaluated at CustomizableViewportOverlay.tsx:232 against the props object
 * built at :217-226, which carries NEITHER `element` NOR `servicesManager`, and whose
 * `formatters` holds ONLY formatDate. _renderOverlayItem builds the full set at :169-174,
 * AFTER the condition has already run. So no condition below may read any of those three.
 * contentF may: it receives overlayItemProps.
 *
 * `props` is also `{...displaySetProps}`, and displaySetProps can be null (:98-112, when the
 * viewport has no display sets), so every read of `instance` / `referenceInstance` is
 * optional-chained.
 */
export const VIEWPORT_OVERLAY_TOP_LEFT = [
  {
    id: 'SeriesDescription',
    inheritsFrom: 'ohif.overlayItem',
    label: '',
    title: 'Series description',
    // PER-IMAGE, not per-display-set (§5.5). Upstream reads referenceInstance alone
    // (viewportOverlayCustomization.tsx:17-20), so it does not track the current image. This
    // is the reason parent §5.4 says the reorder is not cosmetic.
    condition: ({
      instance,
      referenceInstance,
    }: {
      instance?: OverlayInstance;
      referenceInstance?: OverlayInstance;
    }) => instance?.SeriesDescription || referenceInstance?.SeriesDescription,
    // The `|| ''` is develop's and is kept for fidelity, but §5.5's stated reason for it --
    // that it stops OverlayItem's `value === undefined` early return hiding the row -- does
    // not hold on 3.13. OverlayItem tests `displayValue` after formatValue (:374-376), and
    // formatValue('') returns '' (platform/core/src/utils/formatValue.js:8-10), so an empty
    // string renders nothing either way; and the condition above already guarantees one of
    // the two is truthy, so this branch is unreachable.
    contentF: ({
      instance,
      referenceInstance,
    }: {
      instance?: OverlayInstance;
      referenceInstance?: OverlayInstance;
    }) => instance?.SeriesDescription || referenceInstance?.SeriesDescription || '',
  },
];

export const VIEWPORT_OVERLAY_BOTTOM_LEFT = [
  {
    id: 'CursorCoordinates',
    // D6/D7 and plan D-6: absent from OverlayItemComponents (:47-52), so _renderOverlayItem
    // reaches customizationService.transform(item) at :187 and calls contentF at :195.
    // transform() returns the item unchanged when no customization by this id is registered
    // (CustomizationService.ts:664-676). Fork-prefixed rather than develop's
    // 'ohif.overlayItem.cursorCoordinates', so a future upstream key of that name cannot
    // silently capture this item.
    inheritsFrom: 'sycai.overlayItem.cursorCoordinates',
    contentF: (props: CursorCoordinatesItemProps) => <CursorCoordinatesItem {...props} />,
  },
  {
    id: 'WindowLevel',
    inheritsFrom: 'ohif.overlayItem.windowLevel',
    // Plan D-5: upstream's, which develop's array dropped. VOIOverlayItem renders it as the
    // element's title attribute (:393-405) -- a tooltip, not screen text, so D8 does not
    // reach it.
    title: 'Window Level',
  },
  {
    id: 'ZoomLevel',
    inheritsFrom: 'ohif.overlayItem.zoomLevel',
    // Identical on upstream and develop. toolGroupService IS in the condition's props (:224),
    // unlike servicesManager.
    condition: (props: {
      toolGroupService: { getActiveToolForViewport: (viewportId: string) => string };
      viewportId: string;
    }) => props.toolGroupService.getActiveToolForViewport(props.viewportId) === 'Zoom',
  },
  {
    id: 'QACheck',
    inheritsFrom: 'sycai.overlayItem.qaCheck',
    contentF: (props: QACheckItemProps) => <QACheckItem {...props} />,
  },
  {
    id: 'ReferringPhysicianName',
    inheritsFrom: 'ohif.overlayItem',
    label: 'Referring Physician:',
    title: 'Referring Physician Name',
    condition: ({ instance }: { instance?: OverlayInstance }) =>
      instance?.Modality === 'CT' && instance?.ReferringPhysicianName,
    contentF: ({
      instance,
      formatters: { formatPN },
    }: {
      instance: OverlayInstance;
      formatters: { formatPN: (name?: string) => string | undefined };
    }) => formatPN(instance.ReferringPhysicianName),
  },
  {
    id: 'StudyDate',
    inheritsFrom: 'ohif.overlayItem',
    // Was in topLeft with an EMPTY label upstream (:4-11). Both the corner and the label are
    // develop's (§5.5).
    label: 'Study Date:',
    title: 'Study date',
    condition: ({
      instance,
      referenceInstance,
    }: {
      instance?: OverlayInstance;
      referenceInstance?: OverlayInstance;
    }) => instance?.StudyDate || referenceInstance?.StudyDate,
    contentF: ({
      instance,
      referenceInstance,
      formatters: { formatDate },
    }: {
      instance?: OverlayInstance;
      referenceInstance?: OverlayInstance;
      formatters: { formatDate: (date: string) => string };
    }) => {
      const studyDate = instance?.StudyDate || referenceInstance?.StudyDate;
      return studyDate ? formatDate(studyDate) : '';
    },
  },
];
